import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Page,
  Card,
  BlockStack,
  TextField,
  Button,
  Banner,
  InlineStack,
  Text,
  SkeletonBodyText,
} from '@shopify/polaris';
import {
  getOrBootstrapShop,
  updateConcierge,
  syncPolicyKnowledge,
  INSTALL_STATUS,
  MAX_SUGGESTED_QUESTIONS,
  type ConciergeUpdate,
  type PolicySyncResult,
  type ShopMe,
} from '../services/brilio';

/**
 * The merchant-editable assistant settings the live backend supports:
 *   - agent_name          (the name in the storefront widget's header)
 *   - greeting            (the first message shown to a shopper)
 *   - persona             (the assistant's system prompt / brand voice)
 *   - suggested_questions (conversation starters offered before they type)
 *   - is_active           (the pause switch — its own card, see below)
 *   - handoff_email       (where a shopper's request for a person is emailed)
 * Backed by GET /shopify/admin/shops/me + PATCH /shopify/admin/shops/me/concierge.
 */

/** Drop the empty rows the editor always has, and trim the rest. */
function cleanQuestions(questions: string[]): string[] {
  return questions.map((q) => q.trim()).filter(Boolean);
}

function sameQuestions(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((q, i) => q === b[i]);
}

/**
 * A blank box is "notify nobody" and is perfectly valid. Anything else has to
 * look like an address, because the backend answers a bad one with a 422 and
 * the save banner here is generic — the merchant would read "Couldn't save"
 * with no clue which of five fields was wrong.
 *
 * Deliberately shape-only. The backend's `email_validator` is stricter (it
 * refuses reserved domains like `.test` and `.local` even with deliverability
 * checks off), so this catches the typo and lets the server own the rest.
 */
function handoffEmailError(value: string): string | undefined {
  const v = value.trim();
  if (!v) return undefined;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)
    ? undefined
    : 'Enter a valid email address, or leave this empty to turn notifications off.';
}

/**
 * Teach the assistant the store's own policies.
 *
 * Two of the three conversation starters shipped with the app — returns and
 * shipping — cannot be answered from products alone, and the backend that
 * indexes them has been callable but unreachable: there was no control in the
 * admin, so no merchant could ever run it and the chips stayed dead.
 *
 * Deliberately merchant-triggered rather than automatic. Reading the pages
 * costs credits, and the outcome depends on facts about the store and the
 * install (are any policies written? was the app approved to read them?) that
 * are better reported than silently retried.
 */
export function PolicyKnowledgeCard({ indexed }: { indexed?: number }) {
  const qc = useQueryClient();
  const [result, setResult] = useState<PolicySyncResult | null>(null);

  const sync = useMutation({
    mutationFn: syncPolicyKnowledge,
    onSuccess: (res) => {
      setResult(res);
      // The count on the shop payload changes when records are created, so the
      // card's own "already indexed" line must not go stale behind the result.
      qc.invalidateQueries({ queryKey: ['shop', 'me'] });
    },
  });

  // Every outcome below is a 200. Only `indexed` did work; the rest are facts
  // about the merchant's store, each with a different next step — so they get
  // informational tones, not the red banner that says "you broke something".
  const tone =
    result?.status === 'indexed'
      ? 'success'
      : result?.status === 'failed'
      ? 'critical'
      : 'info';

  return (
    <Card>
      <BlockStack gap="300">
        <BlockStack gap="100">
          <Text as="h3" variant="headingSm">
            Store policies
          </Text>
          <Text as="p" tone="subdued" variant="bodySm">
            {indexed
              ? `Your assistant can answer from ${indexed} store ${
                  indexed === 1 ? 'policy' : 'policies'
                }. Run this again after you edit a policy.`
              : "Your assistant can't answer questions about returns or shipping yet. Read your store policies so it can."}
          </Text>
        </BlockStack>

        {result ? (
          <Banner tone={tone} onDismiss={() => setResult(null)}>
            {result.message}
          </Banner>
        ) : null}
        {sync.isError ? (
          <Banner tone="critical">Couldn&rsquo;t reach your store just now — please try again.</Banner>
        ) : null}

        <InlineStack align="start">
          <Button
            loading={sync.isPending}
            disabled={sync.isPending}
            onClick={() => {
              setResult(null);
              sync.mutate();
            }}
          >
            {indexed ? 'Re-read my policies' : 'Read my policies'}
          </Button>
        </InlineStack>
      </BlockStack>
    </Card>
  );
}

/**
 * The id App Bridge addresses the save bar by; it must match the element's own
 * `id` attribute. `shopify.saveBar.show()` takes an id, not a ref — a mismatched
 * one fails silently, leaving the merchant no way to save and nothing in the
 * console to say why.
 */
const SAVE_BAR_ID = 'settings-save-bar';

/** The save bar half of the App Bridge global — Shopify's own type for it. */
type SaveBarApi = typeof window.shopify.saveBar;

/**
 * The admin's contextual save bar, or null when this page is not inside one.
 *
 * Two separate facts have to hold, and collapsing them into one is how this goes
 * wrong:
 *
 *  - `host` is in the URL. That is the app's own test for "embedded in the
 *    admin" (main.tsx boots App Bridge on it; config/shopify-app-bridge.ts names
 *    it), and the global does not imply it: index.html loads the App Bridge CDN
 *    script unconditionally, so `shopify` can exist on a page that is not
 *    embedded at all — a local dev preview, the .shots rig — where the bar can
 *    never be drawn.
 *  - `show` AND `hide` are both callable. Every member of the vendor's
 *    `_SaveBarApi` is optional, and only `SaveBarApi extends Required<...>` makes
 *    them required, so Shopify itself models them as maybe-absent. Testing the
 *    object alone would accept a stub that carries the key and then die at the
 *    call — inside a `useEffect`, which App.tsx's route ErrorBoundary turns into
 *    an error page over a settings edit.
 *
 * The global is re-typed as optional before it is read because App Bridge
 * declares it as a `var` that always exists: on the vendor's types
 * `window.shopify.saveBar.show(...)` compiles and then throws everywhere the
 * script never ran — the same shape as the `redirect.dispatch` bug
 * config/shopify-app-bridge.ts exists to memorialise.
 */
function adminSaveBar(): SaveBarApi | null {
  const host = new URLSearchParams(window.location.search).get('host');
  if (!host) return null;
  const bar = (
    window as unknown as { shopify?: { saveBar?: Partial<SaveBarApi> } }
  ).shopify?.saveBar;
  return bar && typeof bar.show === 'function' && typeof bar.hide === 'function'
    ? (bar as SaveBarApi)
    : null;
}

/**
 * Make the admin ask before Discard throws the merchant's edits away.
 *
 * Set on the element instance because that is the only route that produces what
 * App Bridge accepts. It validates this attribute as a flag — present, with an
 * EMPTY value — and React writes an unknown boolean prop onto a CUSTOM element
 * (unlike the plain `<button loading>` below, which it drops), so
 * `discardConfirmation={true}` in JSX reaches the DOM as
 * `discardconfirmation="true"`. That fails the vendor's own schema check, which
 * re-runs on every attribute write to the element or its buttons — i.e. on
 * every keystroke, since Save's `disabled` tracks the form — so the merchant
 * gets an unarmed Discard and a console error per character typed. The property
 * setter writes the empty value the validator wants.
 *
 * Every branch degrades to doing nothing, and that is load-bearing rather than
 * defensive padding: the component tests put `host` on the URL and stub the
 * bridge, so the element below really is in the document there while nothing
 * ever upgrades it into a custom element.
 *
 *  - no element under that id: nothing to arm. SAVE_BAR_ID above records why an
 *    id that has drifted from the element's own fails silently; this is one
 *    more place where it would;
 *  - an element the browser has not upgraded: `in` walks the prototype chain,
 *    so this is false until App Bridge's accessor exists. Assigning to a
 *    not-yet-upgraded element would instead create an own data property that
 *    shadows that accessor permanently — worse than not writing at all;
 *  - already armed: the setter calls `setAttribute` unconditionally and the
 *    element re-validates and re-publishes itself on every attribute write, so
 *    this check keeps that work off the keystroke path.
 */
function armDiscardConfirmation(): void {
  const bar = document.getElementById(SAVE_BAR_ID);
  if (!bar || !('discardConfirmation' in bar)) return;
  const upgraded = bar as UISaveBarElement;
  if (!upgraded.discardConfirmation) upgraded.discardConfirmation = true;
}

export default function Settings() {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [greeting, setGreeting] = useState('');
  const [persona, setPersona] = useState('');
  const [questions, setQuestions] = useState<string[]>([]);
  const [handoffEmail, setHandoffEmail] = useState('');
  const [saved, setSaved] = useState(false);

  const { data: shop, isLoading, isError } = useQuery({
    queryKey: ['shop', 'me'],
    queryFn: getOrBootstrapShop,
  });

  // Seed the draft ONCE from the first server load. A background refetch (React
  // Query refetches on window focus) must not clobber in-progress edits.
  const seeded = useRef(false);
  // The same fact as `seeded`, held as state rather than a ref because the save
  // bar has to re-render on it — see the mirroring effect below for what goes
  // wrong without it. Set in the same batch as the field values, so no render
  // ever sees the shop loaded next to an empty form.
  const [formSeeded, setFormSeeded] = useState(false);
  useEffect(() => {
    if (shop && !seeded.current) {
      seeded.current = true;
      reseed(shop);
      setFormSeeded(true);
    }
  }, [shop]);

  function reseed(from: ShopMe) {
    setName(from.concierge.agent_name ?? '');
    setGreeting(from.concierge.greeting ?? '');
    setPersona(from.concierge.persona ?? '');
    setQuestions(from.concierge.suggested_questions);
    // Already normalised at the service boundary, so no `??` here: the backend
    // keeps null ("never set") and "" ("switched off") apart, `normaliseConcierge`
    // collapses them to '', and both mean the same empty box.
    setHandoffEmail(from.concierge.handoff_email);
  }

  /**
   * Exactly the fields that differ from the server — the request body AND the
   * dirty check, derived from one place so the Save button can never be enabled
   * for a payload the backend would reject as empty.
   *
   * Blank text fields are omitted rather than sent: the backend enforces
   * `min_length=1`, so clearing a box is "leave it alone", not "erase it".
   * `suggested_questions` is the opposite — an empty list is a real instruction
   * ("show none") — so it is compared, not truthiness-tested.
   */
  const payload = useMemo<ConciergeUpdate>(() => {
    if (!shop) return {};
    const next: ConciergeUpdate = {};
    const n = name.trim();
    const g = greeting.trim();
    const p = persona.trim();
    if (n && n !== (shop.concierge.agent_name ?? '').trim()) next.agent_name = n;
    if (g && g !== (shop.concierge.greeting ?? '').trim()) next.greeting = g;
    if (p && p !== (shop.concierge.persona ?? '').trim()) next.persona = p;
    const q = cleanQuestions(questions);
    if (!sameQuestions(q, shop.concierge.suggested_questions)) {
      next.suggested_questions = q;
    }
    // The opposite of the text fields above: a BLANK box is a real instruction
    // here — "stop emailing me" — so it is compared, not truthiness-tested.
    // When the server never had a value the normalised form is '' too, so a
    // blank box correctly counts as no edit.
    const h = handoffEmail.trim();
    if (h !== shop.concierge.handoff_email) next.handoff_email = h;
    return next;
  }, [shop, name, greeting, persona, questions, handoffEmail]);

  const dirty = Object.keys(payload).length > 0;

  const save = useMutation({
    mutationFn: () => updateConcierge(payload),
    onSuccess: (updated) => {
      qc.setQueryData(['shop', 'me'], updated);
      // Reseed from the server, not from the draft: the backend normalises the
      // starters (collapses whitespace, drops duplicates), so the saved list can
      // legitimately differ from what was typed.
      reseed(updated);
      setSaved(true);
    },
  });

  // The pause switch is its own mutation on purpose. Folding "take my assistant
  // offline" into the same Save button as a typo fix means an unrelated edit can
  // carry it along — and it would stay pending behind unsaved text.
  const toggleActive = useMutation({
    mutationFn: (next: boolean) => updateConcierge({ is_active: next }),
    onSuccess: (updated) => {
      qc.setQueryData(['shop', 'me'], updated);
      setSaved(false);
    },
  });

  // Every editable control is frozen while a save is in flight. `payload` is
  // captured at click time, so a keystroke landing during the request is not in
  // the body — and `onSuccess` then reseeds the whole form from the response,
  // overwriting it. The merchant would be shown "Saved." over an edit that was
  // never sent and has just been wiped off the screen. Freezing the inputs is
  // the honest fix: the reseed itself is load-bearing (the backend normalises
  // the starters, so the saved list can legitimately differ from what was
  // typed), so the window has to be closed at the input, not at the reseed.
  //
  // The two mutations are mutually exclusive for the same reason: both write the
  // `['shop', 'me']` cache key, so letting them overlap lets the slower response
  // reseed the form from a shop view that predates the other's change.
  const saving = save.isPending || toggleActive.isPending;

  function touched() {
    setSaved(false);
    // Editing clears a prior failed-save banner so it isn't stale.
    if (save.isError) save.reset();
  }

  // Where the merchant saves from. Inside the admin that is Shopify's own
  // contextual save bar, which is what review criterion 4.1.5 asks for; outside
  // it, the in-page button further down. Exactly one of the two is ever rendered
  // — a second Save beside the bar is the review finding itself, and no Save at
  // all would leave the form unsavable wherever App Bridge is absent, which is
  // how the component tests and the listing screenshot rig run this page.
  //
  // `barRefused` is the belt to that pair of braces: when the page IS embedded
  // and App Bridge IS loaded but the host still refuses to draw the bar, the
  // rejected `show()` puts the in-page footer back. Without it that merchant has
  // a Settings form with no Save anywhere on it and nothing in the console.
  const [barRefused, setBarRefused] = useState(false);
  // Read on every render rather than captured once: index.html loads App Bridge
  // synchronously before main.tsx, so inside the admin it is already there for
  // the first render, and outside it never arrives at all.
  const saveBar = barRefused ? null : adminSaveBar();

  // Mirror the form's dirty state onto the bar. Driven by `dirty` rather than
  // from the field handlers, so the bar and the request body come from the one
  // payload: an edit typed and then typed back out leaves nothing to save and
  // must take the bar away again. It is also what hides the bar after a save and
  // after a discard, since both reseed the form from the last saved shop.
  //
  // Nothing is mirrored before the form has been seeded. There is exactly one
  // render between the shop arriving and the seeding effect filling the boxes,
  // and in it the form is empty while the shop is not — so `payload` reads
  // "remove every starter, clear the handoff address" and `dirty` is true. Left
  // ungated, the bar animates into the admin's chrome and straight back out on
  // every visit to this page.
  //
  // A background refetch (React Query refetches on window focus) that returns
  // changed settings can also flip `dirty` with no merchant keystroke, and will
  // now raise the bar where it used to raise only the "Unsaved changes" label.
  // Left as is: the draft really does differ from the server at that point, and
  // silently hiding that is worse than showing it.
  useEffect(() => {
    if (!saveBar || !formSeeded) return;
    // Before the show, never after: the admin draws the bar's Discard button
    // itself and reads this flag off the element as the bar becomes visible.
    // It lives in this effect rather than one of its own because `dirty`
    // turning true is the only thing that ever raises the bar, and that re-runs
    // this effect — so the flag is always in place first.
    armDiscardConfirmation();
    // `Promise.resolve(...)` rather than `.catch` on the return value directly:
    // these are somebody else's runtime, and a `.catch` on a non-promise is a
    // TypeError raised from inside an effect — which App.tsx's route
    // ErrorBoundary turns into an error page over a settings edit.
    Promise.resolve(
      dirty ? saveBar.show(SAVE_BAR_ID) : saveBar.hide(SAVE_BAR_ID),
    ).catch(() => {
      // Only a refused SHOW matters: it is the case where the merchant would
      // otherwise be left with no Save at all, so fall back to the in-page one.
      // A refused hide is nothing to recover from — the bar is already gone.
      if (dirty) setBarRefused(true);
    });
  }, [saveBar, formSeeded, dirty]);

  // Leaving Settings has to take the bar with it. It is drawn in the admin's own
  // chrome, not in this tree, so unmounting the page does not remove it: the
  // merchant lands on the Dashboard still being offered a Save for a form that
  // is no longer on screen. App Bridge's own <SaveBar> wrapper hides on unmount
  // for the same reason.
  useEffect(() => {
    if (!saveBar) return;
    return () => {
      Promise.resolve(saveBar.hide(SAVE_BAR_ID)).catch(() => {});
    };
  }, [saveBar]);

  if (isLoading) {
    return (
      <Page title="Settings">
        <SkeletonBodyText lines={6} />
      </Page>
    );
  }

  // A failed shop query (401/5xx) resolves loading with no data — surface an
  // explicit error instead of spinning on the skeleton forever.
  if (isError || !shop) {
    return (
      <Page title="Settings">
        <Banner tone="critical">Unable to load your shop. Try reloading.</Banner>
      </Page>
    );
  }

  // The backend rejects edits to these settings with HTTP 409 unless the shop
  // is "active" (update_concierge_settings gates on install_status + agent_id),
  // so an editor here could only ever fail. Don't render it for non-active
  // shops — show the shop's state and point back to the Dashboard instead,
  // which is the page the copy below actually names.
  if (shop.install_status !== INSTALL_STATUS.active) {
    const installing = shop.install_status === INSTALL_STATUS.installing;
    return (
      <Page title="Settings">
        <Banner tone={installing ? 'info' : 'warning'}>
          {installing
            ? "Your assistant is still being set up. You'll be able to edit its settings once setup finishes — check the Dashboard page for progress."
            : `Editing is unavailable while the assistant isn't active (status: ${shop.install_status}). See the Dashboard page for status and next steps.`}
        </Banner>
      </Page>
    );
  }

  const live = shop.concierge.is_active;
  const canAddQuestion = questions.length < MAX_SUGGESTED_QUESTIONS;
  const handoffError = handoffEmailError(handoffEmail);
  const canSave = dirty && !saving && !handoffError;

  // Guarded here, not only by the button's `disabled`: that attribute is handed
  // to an element App Bridge re-renders in the admin's chrome, and whether it
  // honours it is undocumented — too thin a thing to hang a guaranteed 422 on.
  const saveEdits = () => {
    if (!canSave) return;
    save.mutate();
  };

  // Discard restores the LAST SAVED settings, and that is exactly what `shop`
  // holds: the save mutation writes the server's response into this cache entry.
  // So this is "put back what is stored", never "empty the form".
  const discardEdits = () => {
    reseed(shop);
    touched();
  };

  return (
    <Page
      title="Settings"
      subtitle={`Editing the ${shop.concierge.agent_name || 'assistant'} for ${shop.shop_domain}.`}
    >
      {/* Inert markup on its own: App Bridge takes these two buttons over and
          draws them in the admin's chrome, so none of this appears in the page.
          It is rendered only where App Bridge can host it, which is why the
          screenshot rig and the component tests never see stray buttons. */}
      {saveBar ? (
        <ui-save-bar id={SAVE_BAR_ID}>
          <button
            variant="primary"
            onClick={saveEdits}
            disabled={!canSave}
            // `''`, never `true`: React refuses to write a boolean to an
            // attribute it does not know, dropping it with a console warning —
            // so `loading={true}` leaves the merchant pressing a button that
            // never shows it is working.
            loading={save.isPending ? '' : undefined}
          >
            Save
          </button>
          <button onClick={discardEdits} disabled={saving}>
            Discard
          </button>
        </ui-save-bar>
      ) : null}

      <BlockStack gap="400">
        {saved && !dirty ? (
          <Banner tone="success" onDismiss={() => setSaved(false)}>
            Saved.
          </Banner>
        ) : null}
        {save.isError ? (
          <Banner tone="critical">Couldn't save — please try again.</Banner>
        ) : null}

        <Card>
          <BlockStack gap="400">
            <TextField
              label="Assistant name"
              helpText="Shown in the chat window's header on your storefront."
              value={name}
              onChange={(v) => {
                setName(v);
                touched();
              }}
              maxLength={60}
              showCharacterCount
              autoComplete="off"
              disabled={saving}
            />
            <TextField
              label="Greeting"
              helpText="The first message the assistant shows a shopper."
              value={greeting}
              onChange={(v) => {
                setGreeting(v);
                touched();
              }}
              multiline={2}
              autoComplete="off"
              disabled={saving}
            />
            <TextField
              label="Persona"
              helpText="How the assistant talks — its brand voice and behaviour."
              value={persona}
              onChange={(v) => {
                setPersona(v);
                touched();
              }}
              multiline={6}
              autoComplete="off"
              disabled={saving}
            />
          </BlockStack>
        </Card>

        <Card>
          <BlockStack gap="300">
            <BlockStack gap="100">
              <Text as="h3" variant="headingSm">
                Conversation starters
              </Text>
              <Text as="p" tone="subdued" variant="bodySm">
                Up to {MAX_SUGGESTED_QUESTIONS} questions offered as buttons before the
                shopper types. Pick ones your assistant can answer from your products and
                policies. Remove them all to show none.
              </Text>
            </BlockStack>

            {questions.length === 0 ? (
              <Text as="p" tone="subdued" variant="bodySm">
                No starters — shoppers will see just the greeting.
              </Text>
            ) : null}

            {questions.map((question, i) => (
              <InlineStack key={i} gap="200" blockAlign="center" wrap={false}>
                <div style={{ flex: 1 }}>
                  <TextField
                    label={`Starter ${i + 1}`}
                    labelHidden
                    value={question}
                    onChange={(v) => {
                      setQuestions(questions.map((q, j) => (j === i ? v : q)));
                      touched();
                    }}
                    maxLength={120}
                    autoComplete="off"
                    disabled={saving}
                  />
                </div>
                <Button
                  variant="tertiary"
                  disabled={saving}
                  onClick={() => {
                    setQuestions(questions.filter((_, j) => j !== i));
                    touched();
                  }}
                  accessibilityLabel={`Remove starter ${i + 1}`}
                >
                  Remove
                </Button>
              </InlineStack>
            ))}

            <InlineStack align="start">
              <Button
                disabled={saving || !canAddQuestion}
                onClick={() => {
                  setQuestions([...questions, '']);
                  touched();
                }}
              >
                Add a starter
              </Button>
            </InlineStack>
          </BlockStack>
        </Card>

        <Card>
          <BlockStack gap="300">
            <BlockStack gap="100">
              <Text as="h3" variant="headingSm">
                Handoff notifications
              </Text>
              <Text as="p" tone="subdued" variant="bodySm">
                When a shopper asks to speak with a person, we email you their
                address, what they asked and the conversation so far. Leave this
                empty and nothing is sent — you would only see the request by
                opening Conversations.
              </Text>
            </BlockStack>
            <TextField
              label="Email address for handoff requests"
              type="email"
              helpText="Any address your team actually reads — it doesn't have to be your Shopify account email. One email per request, not per tap."
              value={handoffEmail}
              error={handoffError}
              onChange={(v) => {
                setHandoffEmail(v);
                touched();
              }}
              maxLength={254}
              autoComplete="email"
              disabled={saving}
            />
          </BlockStack>
        </Card>

        {/* Only where there is no save bar to save from. Both at once is the
            review finding; neither is a form nobody can save. */}
        {saveBar ? null : (
          <InlineStack align="end" gap="200" blockAlign="center">
            <Text as="span" tone="subdued" variant="bodySm">
              {dirty ? 'Unsaved changes' : ' '}
            </Text>
            <Button
              variant="primary"
              loading={save.isPending}
              disabled={!canSave}
              onClick={saveEdits}
            >
              Save
            </Button>
          </InlineStack>
        )}

        <PolicyKnowledgeCard indexed={shop.policies_indexed} />

        <Card>
          <BlockStack gap="300">
            <BlockStack gap="100">
              <Text as="h3" variant="headingSm">
                Storefront visibility
              </Text>
              <Text as="p" tone="subdued" variant="bodySm">
                {live
                  ? 'Your assistant is answering shoppers. Pausing hides the chat button and stops it replying — your settings, and your data, are kept.'
                  : 'Your assistant is paused: the chat button is hidden and it will not reply. Nothing has been deleted — resume whenever you like.'}
              </Text>
            </BlockStack>
            {toggleActive.isError ? (
              <Banner tone="critical">
                Couldn't change that — please try again.
              </Banner>
            ) : null}
            <InlineStack align="start">
              <Button
                tone={live ? 'critical' : undefined}
                variant={live ? undefined : 'primary'}
                loading={toggleActive.isPending}
                disabled={saving}
                onClick={() => toggleActive.mutate(!live)}
              >
                {live ? 'Pause assistant' : 'Resume assistant'}
              </Button>
            </InlineStack>
          </BlockStack>
        </Card>
      </BlockStack>
    </Page>
  );
}
